<template>
    <div>
        <div
            v-if="subscriptionsObj?.email_address"
            class="mb-4 font-weight-medium"
        >
            {{ subscriptionsObj.email_address }}
        </div>
        <p class="mb-4">
            Select topics below for information you would like to be emailed on.
        </p>
        <form
            v-if="subscriptionsObj"
            @submit="onSubmit($event)"
        >
            <v-row
                dense
            >
                <template
                    v-for="sub in subscriptionsObj.data"
                    :key="sub.key"
                >
                    <v-col
                        v-if="sub.key !== 'all'"
                        cols="12"
                        sm="6"
                        md="4"
                    >
                        <v-checkbox
                            :false-value="true"
                            :true-value="false"
                            v-model="sub.unsubscribed"
                            class="opaque-label"
                            @update:model-value="onItemChange"
                        >
                            <template #label>
                                <span class="text-secondary">{{ sub.name }}</span>
                                
                                <v-chip
                                    v-if="preSelectedItem && sub.key === preSelectedItem"
                                    color="success"
                                    size="small"
                                    class="ms-2 text-uppercase"
                                    label
                                >
                                    Saved
                                </v-chip>
                            </template>
                        </v-checkbox>
                    </v-col>
                </template>
            </v-row>
            <div
                class="d-flex justify-center mt-4 pa-2"
            >
                <v-btn
                    v-if="allUnsubscribed === true"
                    color="primary"
                    size="large"
                    type="button"
                    @click="setNone($event)"
                >
                    Resubscribe
                </v-btn>
                <v-btn
                    v-else-if="allUnsubscribed === false"
                    color="primary"
                    size="large"
                    type="button"
                    @click="setAll($event)"
                >
                    Unsubscribe from all emails
                </v-btn>
            </div>
        </form>
        <div v-else>
            <div
                class="d-flex justify-center mb-16"
            >
                <v-progress-circular indeterminate color="accent" width="6" size="48"></v-progress-circular>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { useSubscriptions, useGlobalAlert } from '~/composables';
    import type {
        SubscriptionSetting,
        SubscriptionSettingsResponse
    } from '~/types';
    import { isError as lo_isError } from 'es-toolkit';
    import type { VCheckbox } from 'vuetify/components';


    const props = defineProps({
        uid: {
            type: String as PropType<string>,
            required: true
        },
        preSelected: {
            type: String as PropType<Nilable<string>>,
            required: false
        }
    });

    const useSubscriptionsObj = useSubscriptions();
    const createGlobalAlert = useGlobalAlert();

    const allUnKey = 'keySubscriptionsListAllUn';
    const allUnsubscribed = useState<Nullable<boolean>>(allUnKey, () => null);
    const preSelectedItem = ref(props.preSelected);

    async function getSubscriptions(): Promise<SubscriptionSettingsResponse | void> {
        const response = await useSubscriptionsObj.getSubscriptions(props.uid);

        if (lo_isError(response)) {
            createGlobalAlert({
                text: 'Could not find your subscriptions. Please try again later.',
                show: true,
                type: 'error'
            });

            return;
        }

        const allUn = allUnsubscribed.value = response.data.find((sub: SubscriptionSetting) => sub.key === 'all')?.unsubscribed ?? false;

        response.data.forEach((sub: SubscriptionSetting) => {
            if (sub.key !== 'all' && allUn) {
                sub.unsubscribed = true;
            }

            // Default "unsubscribe" preselected item
            if (preSelectedItem.value && sub.key === preSelectedItem.value) {
                sub.unsubscribed = true;
            }
        });

        return response;
    }

    const { data: subscriptionsObj, refresh: subscriptionsReload } = useLazyAsyncData<SubscriptionSettingsResponse | void>(
        `keySubscriptionsList`,
        () => getSubscriptions()
    );

    async function save(unsubscribeList: string[]) {
        const response = await useSubscriptionsObj.setSubscriptions(props.uid, unsubscribeList);

        if (lo_isError(response)) {
            createGlobalAlert({
                text: 'Could not save your preferences. Please try again later.',
                show: true,
                type: 'error'
            });

            return;
        }

        await subscriptionsReload();

        createGlobalAlert({
            text: 'Preferences saved!',
            show: true,
            type: 'success'
        });
    }

    async function onSubmit(e: Event) {
        e.preventDefault();
    }

    async function onItemChange() {
        preSelectedItem.value = null;
        const unsubscribeList: string[] = [];
 
        subscriptionsObj.value?.data.forEach((sub: SubscriptionSetting) => {
            if (sub.key !== 'all' && sub.unsubscribed) {
                unsubscribeList.push(sub.key);
            }
        });

        await save(unsubscribeList);
    }

    async function setAll(e: Event): Promise<void> {
        await save(['all']);
    }

    async function setNone(e: Event): Promise<void> {
        await save([]);
    }

    onBeforeUnmount(() => {
        clearNuxtState([allUnKey]);
    });
</script>

<style lang="scss"></style>
